import dayjs from 'dayjs';
import { getUserName } from 'lib/user';
import capitalize from 'lodash/capitalize';
import get from 'lodash/get';
import { createSelector } from 'reselect';
import * as actions from 'store/profile/actions';
import { RootState } from 'store/types';
import { RequestState } from 'modules/types/common';
import * as types from './types';

export const profileSelector = (state: RootState) => state.profile;

export const profileIsFetchingSelector = createSelector(
  profileSelector,
  profile => profile.isFetching
);

export const profileRequestStateSelector = createSelector(
  profileSelector,
  profile => profile.profileRequestState
);

export const profileErrorSelector = createSelector(profileSelector, profile => profile.error);

export const profileInsuranceRequestStateSelector = createSelector(
  profileSelector,
  profile => profile.insuranceRequestState
);

export const profileIsHydratedSelector = createSelector(
  profileSelector,
  profile => profile.isHydrated
);

export const profileLastUpdatedSelector = createSelector(
  profileSelector,
  profile => profile.lastUpdated
);

export const profileConsumerSelector = createSelector(
  profileSelector,
  profile => profile?.consumer
);

export const profileRequestedProxies = createSelector(
  profileSelector,
  profile => profile.requestedProxies
);

export const profileProxyToUpdateSelector = createSelector(
  profileSelector,
  profile => profile.proxyToUpdate
);

export const profileRequestedProxiesLengthSelector = createSelector(
  profileSelector,
  profile => profile.requestedProxies.length
);

export const profileValidationSelector = createSelector(
  profileSelector,
  profile => profile.validation
);

export const profileConsumerFirstNameSelector = createSelector(
  profileConsumerSelector,
  consumer => {
    if (!consumer) {
      return 'Guest';
    }

    return consumer?.firstName || consumer?.givenName;
  }
);

export const profileConsumerDisplayNameSelector = createSelector(
  profileConsumerSelector,
  consumer => {
    if (!consumer) {
      return 'Guest';
    }

    const first = consumer?.firstName?.toLowerCase() || consumer?.givenName?.toLowerCase();
    const last = consumer?.lastName?.toLowerCase();
    return `${capitalize(first)} ${capitalize(last)}`;
  }
);

export const profileConsumerUserNameSelector = createSelector(profileConsumerSelector, consumer =>
  getUserName(consumer)
);

export const consumerIdSelector = (state: RootState): string =>
  get(profileConsumerSelector(state), 'consumerId');

export const cernerIdSelector = createSelector(
  profileConsumerSelector,
  consumer => consumer.cernerPersonId
);

export const profileConsumerBirthDateSelector = createSelector(
  profileConsumerSelector,
  consumer => consumer.dateOfBirth
);

export const profileActiveInsuranceSelector = createSelector(
  profileSelector,
  profile => profile.activeInsurance
);

export const profileInsurancesSelector = createSelector(
  profileSelector,
  (profile): types.Insurance[] => profile.insurances || []
);

export const profileEmailsSelector = createSelector(
  profileSelector,
  (profile): types.Email[] => get(profile, 'consumer.emails') || []
);

export const findPrimaryEmail = (emails: types.Email[] = []) => {
  const primary = emails.find(email => email?.primary === true);
  return primary?.value || emails[0]?.value || undefined;
};

export const consumerPrimaryEmailSelector = createSelector(profileEmailsSelector, emails =>
  findPrimaryEmail(emails)
);

export const profilePhonesSelector = createSelector(
  profileSelector,
  (profile): types.Phone[] => get(profile, 'consumer.phones') || []
);

export const profileAddressesSelector = createSelector(
  profileSelector,
  (profile): types.Address[] => get(profile, 'consumer.addresses') || []
);

export const selectHealthFacetsInsuranceSelector = createSelector(
  profileInsurancesSelector,
  insurances => insurances.find(insurance => insurance.source === 'Facets')
);

export const findInsurancesByConsumerId = (
  consumerId: string,
  insurances: types.Insurance[]
): types.Insurance[] =>
  insurances.filter(ins => (ins.members || []).some(mem => mem.consumerId === consumerId));

// consumerIdSelector could not be the right selector for currentConsumerInsurancesSelector
export const currentConsumerInsurancesSelector = createSelector(
  [consumerIdSelector, profileInsurancesSelector],
  findInsurancesByConsumerId
);

export const profileConsumerInsurancesSelector = createSelector(
  [consumerIdSelector, profileInsurancesSelector],
  findInsurancesByConsumerId
);

export const checkInsuranceIsActive = (ins: types.Insurance): boolean =>
  dayjs(ins.startDate).isSameOrBefore(dayjs()) && dayjs(ins.endDate).isSameOrAfter(dayjs());

export const profileConsumerHasActiveSHInsuranceSelector = createSelector(
  [profileConsumerInsurancesSelector],
  (insurances: types.Insurance[]): boolean =>
    insurances.some(ins => ins.source === 'Facets' && checkInsuranceIsActive(ins))
);

export const profileConsumerMilleniumInsuranceSelector = createSelector(
  [profileConsumerInsurancesSelector],
  (insurances: types.Insurance[]): types.Insurance[] =>
    insurances.filter(ins => ins.source === 'Millennium')
);

export const currentConsumerSHsubscriberIdSelector = createSelector(
  currentConsumerInsurancesSelector,
  insurances => {
    const shInsurance = insurances.find(insurance => insurance.source === 'Facets');
    if (!shInsurance) {
      return null;
    }

    return shInsurance.subscriberId;
  }
);
export const currentConsumerHasInsurancesSelector = createSelector(
  currentConsumerInsurancesSelector,
  (insurances): boolean => insurances && insurances.length > 0
);

export const currentConsumerHasShSubscriberNumber = (state: RootState): boolean =>
  !!get(profileConsumerSelector(state), 'shSubscriberNumber', false);

export const currentConsumerHasSHInsurancesSelector = createSelector(
  [consumerIdSelector, profileInsurancesSelector],
  (consumerId: string, insurances: types.Insurance[]): boolean =>
    insurances.some(
      ins =>
        ins.source === 'Facets' && (ins.members || []).some(mem => mem.consumerId === consumerId)
    )
);

export const hasActiveSHInsurancesSelector = createSelector(
  [profileInsurancesSelector],
  (insurances: types.Insurance[]): boolean =>
    insurances.some(
      ins =>
        ins.source === 'Facets' &&
        dayjs(ins.startDate).isSameOrBefore(dayjs()) &&
        dayjs(ins.endDate).isSameOrAfter(dayjs())
    )
);

// To be safe, check two places to decide if user is SelectHealth Member
// consumer.shSubscriberNumber and Facets insurances.members
export const isSelectHealthMemberSelector = createSelector(
  [currentConsumerHasShSubscriberNumber, currentConsumerHasSHInsurancesSelector],
  (subNumber, shIns) => subNumber || shIns
);

export const isPrimaryUserSelectHealthMemberSelector = createSelector(
  [profileInsurancesSelector, profileConsumerSelector],
  (insurances, consumer) => {
    if (!consumer.shSubscriberNumber) {
      return false;
    }

    return insurances.some(
      ins =>
        ins.source === 'Facets' &&
        (ins.members || []).some(mem => mem.consumerId === consumer.consumerId)
    );
  }
);

export const profileGranteesSelector = createSelector(
  profileSelector,
  profile => profile.grantees || []
);

export const profileSexSelector = createSelector(
  profileConsumerSelector,
  consumer => consumer.gender || undefined
);

export const profileGrantorsSelector = createSelector(
  profileSelector,
  profile => profile?.grantors || []
);

interface GrantBase {
  consumerId: string;
  cernerPersonId?: string;
}

interface Grantor extends GrantBase {
  isInMillenium?: boolean;
}

export const getGrantPersonIdByConsumerId = (
  consumerId: string,
  state: RootState
): string | undefined => {
  const grantors: Grantor[] = profileGrantorsSelector(state);
  const grantees: GrantBase[] = profileGranteesSelector(state);
  const consumers: GrantBase[] = [{ ...(profileConsumerSelector(state) || {}) }];
  return [...grantors, ...grantees, ...consumers].find(
    (rec: GrantBase) => rec.consumerId === consumerId
  )?.cernerPersonId;
};

/**
 * Selects active grantors
 */
export const activeGrantorsSelector = createSelector(profileGrantorsSelector, grantors =>
  grantors.filter((grantor: Grantor) => grantor.isInMillenium)
);

/**
 * Selects the current consumer and their grantors
 */
export const consumersSelector = createSelector(
  [profileConsumerSelector, profileGrantorsSelector],
  (consumer, grantors) => {
    return [consumer, ...grantors];
  }
);

export const consumerIdsSelector = createSelector(consumersSelector, consumers => {
  return consumers.map(c => c.consumerId);
});

export const getConsumerSubscriberByConsumerId = (
  consumerId: string,
  insurances: types.Insurance[]
): types.ConsumerSubscriber[] => {
  const result: types.ConsumerSubscriber[] = [];

  for (const ins of insurances) {
    if (!ins.members) {
      continue;
    }

    for (const member of ins.members) {
      if (member.consumerId === consumerId) {
        result.push({
          consumerId,
          subscriberId: ins.subscriberId,
          suffix: member.suffix
        });
      }
    }
  }

  return result;
};

export const userGeneralTermsAndConditionsSelector = createSelector(
  profileSelector,
  profile => profile?.consumer?.termAcceptance
);

export const userExternalTermsAndConditionsSelector = createSelector(profileSelector, profile =>
  get(profile, 'consumer.externalTermsAcceptance', [])
);

export const profileValidatedSelector = createSelector(
  profileValidationSelector,
  validation => validation && validation.status && validation.status === 'IDW012'
);

export const profileValidationErrorSelector = createSelector(
  profileValidationSelector,
  validation => validation && validation.error
);

export const profileValidationSendErrorSelector = createSelector(
  profileValidationErrorSelector,
  error => error && error.type === actions.SEND_PIN_FAIL
);

export const consumerDoOnboardingSelector = createSelector(
  profileConsumerSelector,
  consumer => consumer.doOnboarding || false
);

export const consumerPhonesSelector = createSelector(
  profileConsumerSelector,
  consumer => consumer.phones || []
);

export function findPrimaryPhone(phones: types.Phone[]) {
  if (!phones || phones.length === 0) {
    return null;
  }

  const result = phones.filter(obj => obj.primary === true);
  if (result && result.length > 0) {
    return result[0].value;
  }
  return null;
}

export const consumerPrimaryPhoneSelector = createSelector(consumerPhonesSelector, phones =>
  findPrimaryPhone(phones)
);

export const consumerListEncryptionKeySelector = createSelector(
  profileConsumerSelector,
  consumer => [consumer.appCacheKey, consumer.newAppCacheKey] || []
);

export const profileVisitpayLoading = createSelector(
  profileSelector,
  (profile: types.ProfileState) => {
    return (
      profile.visitpayEnrolledRequestState !== RequestState.SUCCESS &&
      profile.visitpayEnrolledRequestState !== RequestState.ERROR
    );
  }
);
export const profileVisitpayEnrolled = createSelector(
  profileSelector,
  (profile: types.ProfileState) => {
    return profile.visitpayEnrolledAndAccessible;
  }
);
